import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { KeyRound, Lightbulb, Bell, Gift } from 'lucide-react';
import { UseCase } from '../types';

interface UseCaseLinksProps {
  useCases: UseCase[];
}

const Container = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '300px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const List = styled('ul')({
  position: 'absolute',
  display: 'flex',
  margin: 0,
  padding: 0,
  transform: 'translate(-50%, -50%)',
  left: '50%',
  top: '50%',
});

const ListItem = styled('li')({
  listStyle: 'none',
  margin: '0 5px',
});

const LinkContainer = styled('a')(({ theme }) => ({
  textDecoration: 'none',
  display: 'block',
  width: '210px',
  height: '80px',
  background: '#fff',
  textAlign: 'left',
  paddingLeft: '20px',
  transform: 'rotate(-30deg) skew(25deg) translate(0,0)',
  transition: '.5s',
  boxShadow: '-20px 20px 10px rgba(0,0,0,.5)',
  position: 'relative',
  cursor: 'pointer',

  '&:before': {
    content: '""',
    position: 'absolute',
    top: '10px',
    left: '-20px',
    height: '100%',
    width: '20px',
    background: '#b1b1b1',
    transform: 'rotate(0deg) skewY(-45deg)',
    transition: '.5s',
  },

  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: '-20px',
    left: '-10px',
    height: '20px',
    width: '100%',
    background: '#b1b1b1',
    transform: 'rotate(0deg) skewX(-45deg)',
    transition: '.5s',
  },

  '&:hover': {
    transform: 'rotate(-30deg) skew(25deg) translate(20px,-15px)',
    boxShadow: '-50px 50px 50px rgba(0,0,0,.5)',
    background: '#8a2be2',

    '&:before': {
      background: '#6a1b9a',
    },

    '&:after': {
      background: '#9c27b0',
    },

    '& .icon': {
      color: '#00ffff',
      filter: 'drop-shadow(0 0 8px rgba(0, 255, 255, 0.5))',
    },

    '& .text': {
      color: '#00ffff',
      textShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
    },
  },
}));

const IconWrapper = styled(Box)({
  fontSize: '40px',
  color: 'rgba(2, 2, 2, 0.9)',
  lineHeight: '80px',
  transition: '.5s',
  paddingRight: '14px',
  display: 'flex',
  alignItems: 'center',
});

const Text = styled(Typography)({
  padding: 0,
  margin: 0,
  position: 'absolute',
  top: '30px',
  left: '80px',
  color: 'rgba(2, 2, 2, 0.9)',
  letterSpacing: '4px',
  transition: '.5s',
  fontSize: '14px',
  fontWeight: 500,
  textShadow: '0 0 5px rgba(138, 43, 226, 0.3)',
});

const getIcon = (index: number) => {
  switch (index) {
    case 0:
      return <KeyRound size={32} className="icon" />;
    case 1:
      return <Lightbulb size={32} className="icon" />;
    case 2:
      return <Bell size={32} className="icon" />;
    case 3:
      return <Gift size={32} className="icon" />;
    default:
      return <Lightbulb size={32} className="icon" />;
  }
};

const UseCaseLinks: React.FC<UseCaseLinksProps> = ({ useCases }) => {
  return (
    <Container>
      <List>
        {useCases.map((useCase, index) => (
          <ListItem key={useCase.id}>
            <LinkContainer>
              <IconWrapper className="icon">
                {getIcon(index)}
              </IconWrapper>
              <Text className="text">
                {useCase.title.split(' ')[0]}
              </Text>
            </LinkContainer>
          </ListItem>
        ))}
      </List>
    </Container>
  );
};

export default UseCaseLinks; 